import { useEffect, useState, useRef, useMemo } from "react";
import { UserPlus, Save, X } from "lucide-react";
import api from "../services/api";
import Swal from "sweetalert2";

export default function ClientForm({
  fetchClients,
  editingClient,
  setEditingClient,
  onCancel,
}) {
  const nombreRef = useRef(null);
  const isLoadingClient = useRef(false);

  const [nombre, setNombre] = useState("");
  const [nrc, setNrc] = useState("");
  const [direccion, setDireccion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [tipoCliente, setTipoCliente] = useState("No Contribuyente");
  const [departamento, setDepartamento] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [distrito, setDistrito] = useState("");
  const [nombreComercial, setNombreComercial] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [codActividad, setCodActividad] = useState("");
  const [descActividad, setDescActividad] = useState("");
  const [actividades, setActividades] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(false);
  const [actividadSearch, setActividadSearch] = useState('');
  const [showActividadDropdown, setShowActividadDropdown] = useState(false);
  const [filteredActividades, setFilteredActividades] = useState([]);

  // ========================================
  // ✅ FUNCIONES DE FORMATEO
  // ========================================
  
  const validarNIT = (nit) => {
    const nitLimpio = nit.replace(/-/g, '');
    const formato14 = /^\d{14}$/;
    const formato9 = /^\d{9}$/;
    return formato14.test(nitLimpio) || formato9.test(nitLimpio);
  };

  const formatearNIT = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    
    if (numeros.length <= 9) {
      if (numeros.length > 8) {
        return `${numeros.slice(0, 8)}-${numeros.slice(8)}`;
      }
      return numeros;
    } else {
      if (numeros.length > 13) {
        return `${numeros.slice(0, 4)}-${numeros.slice(4, 10)}-${numeros.slice(10, 13)}-${numeros.slice(13, 14)}`;
      } else if (numeros.length > 10) {
        return `${numeros.slice(0, 4)}-${numeros.slice(4, 10)}-${numeros.slice(10)}`;
      } else if (numeros.length > 4) {
        return `${numeros.slice(0, 4)}-${numeros.slice(4)}`;
      }
      return numeros;
    }
  };

  const formatearDUI = (valor) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length > 8) {
      return `${numeros.slice(0, 8)}-${numeros.slice(8, 9)}`;
    }
    return numeros;
  };

  // ========================================
  // ✅ CATÁLOGOS OFICIALES DE HACIENDA
  // ========================================

  const tiposDocumento = [
    { codigo: "36", nombre: "NIT" },
    { codigo: "13", nombre: "DUI" },
    { codigo: "03", nombre: "Pasaporte" },
    { codigo: "02", nombre: "Carnet de Residente" },
    { codigo: "37", nombre: "Otro" },
  ];

  const departamentos = [
    { codigo: "00", nombre: "Otro (Para extranjeros)" },
    { codigo: "01", nombre: "AHUACHAPAN" },
    { codigo: "02", nombre: "SANTA ANA" },
    { codigo: "03", nombre: "SONSONATE" },
    { codigo: "04", nombre: "CHALATENANGO" },
    { codigo: "05", nombre: "LA LIBERTAD" },
    { codigo: "06", nombre: "SAN SALVADOR" },
    { codigo: "07", nombre: "CUSCATLAN" },
    { codigo: "08", nombre: "LA PAZ" },
    { codigo: "09", nombre: "CABAÑAS" },
    { codigo: "10", nombre: "SAN VICENTE" },
    { codigo: "11", nombre: "USULUTAN" },
    { codigo: "12", nombre: "SAN MIGUEL" },
    { codigo: "13", nombre: "MORAZAN" },
    { codigo: "14", nombre: "LA UNION" },
  ];

  // ✅ CAT-013 OFICIAL - Municipios con códigos que se repiten entre departamentos
  const todosMunicipios = [
    { departamento: "00", codigo: "00", nombre: "Otro (Para extranjeros)" },
    
    // AHUACHAPÁN (01)
    { departamento: "01", codigo: "13", nombre: "AHUACHAPAN NORTE" },
    { departamento: "01", codigo: "14", nombre: "AHUACHAPAN CENTRO" },
    { departamento: "01", codigo: "15", nombre: "AHUACHAPAN SUR" },
    
    // SANTA ANA (02)
    { departamento: "02", codigo: "14", nombre: "SANTA ANA NORTE" },
    { departamento: "02", codigo: "15", nombre: "SANTA ANA CENTRO" },
    { departamento: "02", codigo: "16", nombre: "SANTA ANA ESTE" },
    { departamento: "02", codigo: "17", nombre: "SANTA ANA OESTE" },
    
    // SONSONATE (03)
    { departamento: "03", codigo: "17", nombre: "SONSONATE NORTE" },
    { departamento: "03", codigo: "18", nombre: "SONSONATE CENTRO" },
    { departamento: "03", codigo: "19", nombre: "SONSONATE ESTE" },
    { departamento: "03", codigo: "20", nombre: "SONSONATE OESTE" },
    
    // CHALATENANGO (04)
    { departamento: "04", codigo: "34", nombre: "CHALATENANGO NORTE" },
    { departamento: "04", codigo: "35", nombre: "CHALATENANGO CENTRO" },
    { departamento: "04", codigo: "36", nombre: "CHALATENANGO SUR" },
    
    // LA LIBERTAD (05)
    { departamento: "05", codigo: "23", nombre: "LA LIBERTAD NORTE" },
    { departamento: "05", codigo: "24", nombre: "LA LIBERTAD CENTRO" },
    { departamento: "05", codigo: "25", nombre: "LA LIBERTAD OESTE" },
    { departamento: "05", codigo: "26", nombre: "LA LIBERTAD ESTE" },
    { departamento: "05", codigo: "27", nombre: "LA LIBERTAD COSTA" },
    { departamento: "05", codigo: "28", nombre: "LA LIBERTAD SUR" },
    
    // SAN SALVADOR (06)
    { departamento: "06", codigo: "20", nombre: "SAN SALVADOR NORTE" },
    { departamento: "06", codigo: "21", nombre: "SAN SALVADOR OESTE" },
    { departamento: "06", codigo: "22", nombre: "SAN SALVADOR ESTE" },
    { departamento: "06", codigo: "23", nombre: "SAN SALVADOR CENTRO" },
    { departamento: "06", codigo: "24", nombre: "SAN SALVADOR SUR" },
    
    // CUSCATLÁN (07)
    { departamento: "07", codigo: "17", nombre: "CUSCATLAN NORTE" },
    { departamento: "07", codigo: "18", nombre: "CUSCATLAN SUR" },
    
    // LA PAZ (08)
    { departamento: "08", codigo: "23", nombre: "LA PAZ OESTE" },
    { departamento: "08", codigo: "24", nombre: "LA PAZ CENTRO" },
    { departamento: "08", codigo: "25", nombre: "LA PAZ ESTE" },
    
    // CABAÑAS (09)
    { departamento: "09", codigo: "10", nombre: "CABAÑAS OESTE" },
    { departamento: "09", codigo: "11", nombre: "CABAÑAS ESTE" },
    
    // SAN VICENTE (10)
    { departamento: "10", codigo: "14", nombre: "SAN VICENTE NORTE" },
    { departamento: "10", codigo: "15", nombre: "SAN VICENTE SUR" },
    
    // USULUTÁN (11)
    { departamento: "11", codigo: "24", nombre: "USULUTAN NORTE" },
    { departamento: "11", codigo: "25", nombre: "USULUTAN ESTE" },
    { departamento: "11", codigo: "26", nombre: "USULUTAN OESTE" },
    
    // SAN MIGUEL (12)
    { departamento: "12", codigo: "21", nombre: "SAN MIGUEL NORTE" },
    { departamento: "12", codigo: "22", nombre: "SAN MIGUEL CENTRO" },
    { departamento: "12", codigo: "23", nombre: "SAN MIGUEL OESTE" },
    
    // MORAZÁN (13)
    { departamento: "13", codigo: "27", nombre: "MORAZAN NORTE" },
    { departamento: "13", codigo: "28", nombre: "MORAZAN SUR" },
    
    // LA UNIÓN (14)
    { departamento: "14", codigo: "19", nombre: "LA UNION NORTE" },
    { departamento: "14", codigo: "20", nombre: "LA UNION SUR" },
  ];

  // ✅ Distritos (CAT-008, pueblo específico) — con su municipio (zona, CAT-013)
  // Mapeo oficial según Decreto Legislativo 762 (Ley Especial de Reestructuración Municipal)
  // Mismo catálogo usado en SupplierForm, adaptado a la convención de nombres de este archivo.
  const todosDistritos = [
    { departamento: "00", municipio: "00", codigo: "00", nombre: "Otro (Para extranjeros)" },
    { departamento: "01", municipio: "14", codigo: "01", nombre: "AHUACHAPÁN" },
    { departamento: "01", municipio: "14", codigo: "02", nombre: "APANECA" },
    { departamento: "01", municipio: "13", codigo: "03", nombre: "ATIQUIZAYA" },
    { departamento: "01", municipio: "14", codigo: "04", nombre: "CONCEPCIÓN DE ATACO" },
    { departamento: "01", municipio: "13", codigo: "05", nombre: "EL REFUGIO" },
    { departamento: "01", municipio: "15", codigo: "06", nombre: "GUAYMANGO" },
    { departamento: "01", municipio: "15", codigo: "07", nombre: "JUJUTLA" },
    { departamento: "01", municipio: "15", codigo: "08", nombre: "SAN FRANCISCO MENÉNDEZ" },
    { departamento: "01", municipio: "13", codigo: "09", nombre: "SAN LORENZO" },
    { departamento: "01", municipio: "15", codigo: "10", nombre: "SAN PEDRO PUXTLA" },
    { departamento: "01", municipio: "14", codigo: "11", nombre: "TACUBA" },
    { departamento: "01", municipio: "13", codigo: "12", nombre: "TURÍN" },
    { departamento: "02", municipio: "17", codigo: "01", nombre: "CANDELARIA DE LA FRONTERA" },
    { departamento: "02", municipio: "16", codigo: "02", nombre: "COATEPEQUE" },
    { departamento: "02", municipio: "17", codigo: "03", nombre: "CHALCHUAPA" },
    { departamento: "02", municipio: "16", codigo: "04", nombre: "EL CONGO" },
    { departamento: "02", municipio: "17", codigo: "05", nombre: "EL PORVENIR" },
    { departamento: "02", municipio: "14", codigo: "06", nombre: "MASAHUAT" },
    { departamento: "02", municipio: "14", codigo: "07", nombre: "METAPÁN" },
    { departamento: "02", municipio: "17", codigo: "08", nombre: "SAN ANTONIO PAJONAL" },
    { departamento: "02", municipio: "17", codigo: "09", nombre: "SAN SEBASTIÁN SALITRILLO" },
    { departamento: "02", municipio: "15", codigo: "10", nombre: "SANTA ANA" },
    { departamento: "02", municipio: "14", codigo: "11", nombre: "STA ROSA GUACHI" },
    { departamento: "02", municipio: "17", codigo: "12", nombre: "STGO D LA FRONT" },
    { departamento: "02", municipio: "14", codigo: "13", nombre: "TEXISTEPEQUE" },
    { departamento: "03", municipio: "20", codigo: "01", nombre: "ACAJUTLA" },
    { departamento: "03", municipio: "19", codigo: "02", nombre: "ARMENIA" },
    { departamento: "03", municipio: "19", codigo: "03", nombre: "CALUCO" },
    { departamento: "03", municipio: "19", codigo: "04", nombre: "CUISNAHUAT" },
    { departamento: "03", municipio: "19", codigo: "05", nombre: "STA I ISHUATAN" },
    { departamento: "03", municipio: "19", codigo: "06", nombre: "IZALCO" },
    { departamento: "03", municipio: "17", codigo: "07", nombre: "JUAYÚA" },
    { departamento: "03", municipio: "17", codigo: "08", nombre: "NAHUIZALCO" },
    { departamento: "03", municipio: "18", codigo: "09", nombre: "NAHULINGO" },
    { departamento: "03", municipio: "17", codigo: "10", nombre: "SALCOATITÁN" },
    { departamento: "03", municipio: "18", codigo: "11", nombre: "SAN ANTONIO DEL MONTE" },
    { departamento: "03", municipio: "19", codigo: "12", nombre: "SAN JULIÁN" },
    { departamento: "03", municipio: "17", codigo: "13", nombre: "STA C MASAHUAT" },
    { departamento: "03", municipio: "18", codigo: "14", nombre: "SANTO DOMINGO GUZMÁN" },
    { departamento: "03", municipio: "18", codigo: "15", nombre: "SONSONATE" },
    { departamento: "03", municipio: "18", codigo: "16", nombre: "SONZACATE" },
    { departamento: "04", municipio: "35", codigo: "01", nombre: "AGUA CALIENTE" },
    { departamento: "04", municipio: "36", codigo: "02", nombre: "ARCATAO" },
    { departamento: "04", municipio: "36", codigo: "03", nombre: "AZACUALPA" },
    { departamento: "04", municipio: "34", codigo: "04", nombre: "CITALÁ" },
    { departamento: "04", municipio: "36", codigo: "05", nombre: "COMALAPA" },
    { departamento: "04", municipio: "36", codigo: "06", nombre: "CONCEPCIÓN QUEZALTEPEQUE" },
    { departamento: "04", municipio: "36", codigo: "07", nombre: "CHALATENANGO" },
    { departamento: "04", municipio: "35", codigo: "08", nombre: "DULCE NOM MARÍA" },
    { departamento: "04", municipio: "36", codigo: "09", nombre: "EL CARRIZAL" },
    { departamento: "04", municipio: "35", codigo: "10", nombre: "EL PARAÍSO" },
    { departamento: "04", municipio: "36", codigo: "11", nombre: "LA LAGUNA" },
    { departamento: "04", municipio: "34", codigo: "12", nombre: "LA PALMA" },
    { departamento: "04", municipio: "35", codigo: "13", nombre: "LA REINA" },
    { departamento: "04", municipio: "36", codigo: "14", nombre: "LAS VUELTAS" },
    { departamento: "04", municipio: "36", codigo: "15", nombre: "NOMBRE DE JESUS" },
    { departamento: "04", municipio: "35", codigo: "16", nombre: "NVA CONCEPCIÓN" },
    { departamento: "04", municipio: "36", codigo: "17", nombre: "NUEVA TRINIDAD" },
    { departamento: "04", municipio: "36", codigo: "18", nombre: "OJOS DE AGUA" },
    { departamento: "04", municipio: "36", codigo: "19", nombre: "POTONICO" },
    { departamento: "04", municipio: "36", codigo: "20", nombre: "SAN ANT LA CRUZ" },
    { departamento: "04", municipio: "36", codigo: "21", nombre: "SAN ANT RANCHOS" },
    { departamento: "04", municipio: "34", codigo: "22", nombre: "SAN FERNANDO" },
    { departamento: "04", municipio: "36", codigo: "23", nombre: "SAN FRANCISCO LEMPA" },
    { departamento: "04", municipio: "35", codigo: "24", nombre: "SAN FRANCISCO MORAZÁN" },
    { departamento: "04", municipio: "34", codigo: "25", nombre: "SAN IGNACIO" },
    { departamento: "04", municipio: "36", codigo: "26", nombre: "SAN I LABRADOR" },
    { departamento: "04", municipio: "36", codigo: "27", nombre: "SAN J CANCASQUE" },
    { departamento: "04", municipio: "36", codigo: "28", nombre: "SAN JOSE FLORES" },
    { departamento: "04", municipio: "36", codigo: "29", nombre: "SAN LUIS CARMEN" },
    { departamento: "04", municipio: "36", codigo: "30", nombre: "SN MIG MERCEDES" },
    { departamento: "04", municipio: "35", codigo: "31", nombre: "SAN RAFAEL" },
    { departamento: "04", municipio: "35", codigo: "32", nombre: "SANTA RITA" },
    { departamento: "04", municipio: "35", codigo: "33", nombre: "TEJUTLA" },
    { departamento: "05", municipio: "26", codigo: "01", nombre: "ANTGO CUSCATLÁN" },
    { departamento: "05", municipio: "24", codigo: "02", nombre: "CIUDAD ARCE" },
    { departamento: "05", municipio: "25", codigo: "03", nombre: "COLON" },
    { departamento: "05", municipio: "28", codigo: "04", nombre: "COMASAGUA" },
    { departamento: "05", municipio: "27", codigo: "05", nombre: "CHILTIUPAN" },
    { departamento: "05", municipio: "26", codigo: "06", nombre: "HUIZÚCAR" },
    { departamento: "05", municipio: "25", codigo: "07", nombre: "JAYAQUE" },
    { departamento: "05", municipio: "27", codigo: "08", nombre: "JICALAPA" },
    { departamento: "05", municipio: "27", codigo: "09", nombre: "LA LIBERTAD" },
    { departamento: "05", municipio: "26", codigo: "10", nombre: "NUEVO CUSCATLÁN" },
    { departamento: "05", municipio: "28", codigo: "11", nombre: "SANTA TECLA" },
    { departamento: "05", municipio: "23", codigo: "12", nombre: "QUEZALTEPEQUE" },
    { departamento: "05", municipio: "25", codigo: "13", nombre: "SACACOYO" },
    { departamento: "05", municipio: "26", codigo: "14", nombre: "SN J VILLANUEVA" },
    { departamento: "05", municipio: "24", codigo: "15", nombre: "SAN JUAN OPICO" },
    { departamento: "05", municipio: "23", codigo: "16", nombre: "SAN MATÍAS" },
    { departamento: "05", municipio: "23", codigo: "17", nombre: "SAN P TACACHICO" },
    { departamento: "05", municipio: "27", codigo: "18", nombre: "TAMANIQUE" },
    { departamento: "05", municipio: "25", codigo: "19", nombre: "TALNIQUE" },
    { departamento: "05", municipio: "27", codigo: "20", nombre: "TEOTEPEQUE" },
    { departamento: "05", municipio: "25", codigo: "21", nombre: "TEPECOYO" },
    { departamento: "05", municipio: "26", codigo: "22", nombre: "ZARAGOZA" },
    { departamento: "06", municipio: "20", codigo: "01", nombre: "AGUILARES" },
    { departamento: "06", municipio: "21", codigo: "02", nombre: "APOPA" },
    { departamento: "06", municipio: "23", codigo: "03", nombre: "AYUTUXTEPEQUE" },
    { departamento: "06", municipio: "23", codigo: "04", nombre: "CUSCATANCINGO" },
    { departamento: "06", municipio: "20", codigo: "05", nombre: "EL PAISNAL" },
    { departamento: "06", municipio: "20", codigo: "06", nombre: "GUAZAPA" },
    { departamento: "06", municipio: "22", codigo: "07", nombre: "ILOPANGO" },
    { departamento: "06", municipio: "23", codigo: "08", nombre: "MEJICANOS" },
    { departamento: "06", municipio: "21", codigo: "09", nombre: "NEJAPA" },
    { departamento: "06", municipio: "24", codigo: "10", nombre: "PANCHIMALCO" },
    { departamento: "06", municipio: "24", codigo: "11", nombre: "ROSARIO DE MORA" },
    { departamento: "06", municipio: "24", codigo: "12", nombre: "SAN MARCOS" },
    { departamento: "06", municipio: "22", codigo: "13", nombre: "SAN MARTIN" },
    { departamento: "06", municipio: "23", codigo: "14", nombre: "SAN SALVADOR" },
    { departamento: "06", municipio: "24", codigo: "15", nombre: "STG TEXACUANGOS" },
    { departamento: "06", municipio: "24", codigo: "16", nombre: "SANTO TOMAS" },
    { departamento: "06", municipio: "22", codigo: "17", nombre: "SOYAPANGO" },
    { departamento: "06", municipio: "22", codigo: "18", nombre: "TONACATEPEQUE" },
    { departamento: "06", municipio: "23", codigo: "19", nombre: "CIUDAD DELGADO" },
    { departamento: "07", municipio: "18", codigo: "01", nombre: "CANDELARIA" },
    { departamento: "07", municipio: "18", codigo: "02", nombre: "COJUTEPEQUE" },
    { departamento: "07", municipio: "18", codigo: "03", nombre: "EL CARMEN" },
    { departamento: "07", municipio: "18", codigo: "04", nombre: "EL ROSARIO" },
    { departamento: "07", municipio: "18", codigo: "05", nombre: "MONTE SAN JUAN" },
    { departamento: "07", municipio: "17", codigo: "06", nombre: "ORAT CONCEPCIÓN" },
    { departamento: "07", municipio: "17", codigo: "07", nombre: "SAN B PERULAPIA" },
    { departamento: "07", municipio: "18", codigo: "08", nombre: "SAN CRISTÓBAL" },
    { departamento: "07", municipio: "17", codigo: "09", nombre: "SAN J GUAYABAL" },
    { departamento: "07", municipio: "17", codigo: "10", nombre: "SAN P PERULAPÁN" },
    { departamento: "07", municipio: "18", codigo: "11", nombre: "SAN RAF CEDROS" },
    { departamento: "07", municipio: "18", codigo: "12", nombre: "SAN RAMON" },
    { departamento: "07", municipio: "18", codigo: "13", nombre: "STA C ANALQUITO" },
    { departamento: "07", municipio: "18", codigo: "14", nombre: "STA C MICHAPA" },
    { departamento: "07", municipio: "17", codigo: "15", nombre: "SUCHITOTO" },
    { departamento: "07", municipio: "18", codigo: "16", nombre: "TENANCINGO" },
    { departamento: "08", municipio: "23", codigo: "01", nombre: "CUYULTITÁN" },
    { departamento: "08", municipio: "24", codigo: "02", nombre: "EL ROSARIO" },
    { departamento: "08", municipio: "24", codigo: "03", nombre: "JERUSALÉN" },
    { departamento: "08", municipio: "24", codigo: "04", nombre: "MERCED LA CEIBA" },
    { departamento: "08", municipio: "23", codigo: "05", nombre: "OLOCUILTA" },
    { departamento: "08", municipio: "24", codigo: "06", nombre: "PARAÍSO OSORIO" },
    { departamento: "08", municipio: "23", codigo: "07", nombre: "SN ANT MASAHUAT" },
    { departamento: "08", municipio: "24", codigo: "08", nombre: "SAN EMIGDIO" },
    { departamento: "08", municipio: "23", codigo: "09", nombre: "SN FCO CHINAMEC" },
    { departamento: "08", municipio: "25", codigo: "10", nombre: "SAN J NONUALCO" },
    { departamento: "08", municipio: "23", codigo: "11", nombre: "SAN JUAN TALPA" },
    { departamento: "08", municipio: "24", codigo: "12", nombre: "SAN JUAN TEPEZONTES" },
    { departamento: "08", municipio: "23", codigo: "13", nombre: "SAN LUIS TALPA" },
    { departamento: "08", municipio: "24", codigo: "14", nombre: "SAN MIGUEL TEPEZONTES" },
    { departamento: "08", municipio: "24", codigo: "15", nombre: "SAN PEDRO MASAHUAT" },
    { departamento: "08", municipio: "24", codigo: "16", nombre: "SAN PEDRO NONUALCO" },
    { departamento: "08", municipio: "25", codigo: "17", nombre: "SAN R OBRAJUELO" },
    { departamento: "08", municipio: "24", codigo: "18", nombre: "STA MA OSTUMA" },
    { departamento: "08", municipio: "24", codigo: "19", nombre: "STGO NONUALCO" },
    { departamento: "08", municipio: "23", codigo: "20", nombre: "TAPALHUACA" },
    { departamento: "08", municipio: "25", codigo: "21", nombre: "ZACATECOLUCA" },
    { departamento: "08", municipio: "24", codigo: "22", nombre: "SN LUIS LA HERR" },
    { departamento: "09", municipio: "11", codigo: "01", nombre: "CINQUERA" },
    { departamento: "09", municipio: "10", codigo: "02", nombre: "GUACOTECTI" },
    { departamento: "09", municipio: "11", codigo: "03", nombre: "ILOBASCO" },
    { departamento: "09", municipio: "11", codigo: "04", nombre: "JUTIAPA" },
    { departamento: "09", municipio: "10", codigo: "05", nombre: "SAN ISIDRO" },
    { departamento: "09", municipio: "10", codigo: "06", nombre: "SENSUNTEPEQUE" },
    { departamento: "09", municipio: "11", codigo: "07", nombre: "TEJUTEPEQUE" },
    { departamento: "09", municipio: "10", codigo: "08", nombre: "VICTORIA" },
    { departamento: "09", municipio: "10", codigo: "09", nombre: "DOLORES" },
    { departamento: "10", municipio: "14", codigo: "01", nombre: "APASTEPEQUE" },
    { departamento: "10", municipio: "15", codigo: "02", nombre: "GUADALUPE" },
    { departamento: "10", municipio: "15", codigo: "03", nombre: "SAN CAY ISTEPEQ" },
    { departamento: "10", municipio: "14", codigo: "04", nombre: "SANTA CLARA" },
    { departamento: "10", municipio: "14", codigo: "05", nombre: "SANTO DOMINGO" },
    { departamento: "10", municipio: "14", codigo: "06", nombre: "SN EST CATARINA" },
    { departamento: "10", municipio: "14", codigo: "07", nombre: "SAN ILDEFONSO" },
    { departamento: "10", municipio: "14", codigo: "08", nombre: "SAN LORENZO" },
    { departamento: "10", municipio: "14", codigo: "09", nombre: "SAN SEBASTIÁN" },
    { departamento: "10", municipio: "15", codigo: "10", nombre: "SAN VICENTE" },
    { departamento: "10", municipio: "15", codigo: "11", nombre: "TECOLUCA" },
    { departamento: "10", municipio: "15", codigo: "12", nombre: "TEPETITÁN" },
    { departamento: "10", municipio: "15", codigo: "13", nombre: "VERAPAZ" },
    { departamento: "11", municipio: "24", codigo: "01", nombre: "ALEGRÍA" },
    { departamento: "11", municipio: "24", codigo: "02", nombre: "BERLÍN" },
    { departamento: "11", municipio: "25", codigo: "03", nombre: "CALIFORNIA" },
    { departamento: "11", municipio: "25", codigo: "04", nombre: "CONCEP BATRES" },
    { departamento: "11", municipio: "24", codigo: "05", nombre: "EL TRIUNFO" },
    { departamento: "11", municipio: "25", codigo: "06", nombre: "EREGUAYQUÍN" },
    { departamento: "11", municipio: "24", codigo: "07", nombre: "ESTANZUELAS" },
    { departamento: "11", municipio: "26", codigo: "08", nombre: "JIQUILISCO" },
    { departamento: "11", municipio: "24", codigo: "09", nombre: "JUCUAPA" },
    { departamento: "11", municipio: "25", codigo: "10", nombre: "JUCUARÁN" },
    { departamento: "11", municipio: "24", codigo: "11", nombre: "MERCEDES UMAÑA" },
    { departamento: "11", municipio: "24", codigo: "12", nombre: "NUEVA GRANADA" },
    { departamento: "11", municipio: "25", codigo: "13", nombre: "OZATLÁN" },
    { departamento: "11", municipio: "26", codigo: "14", nombre: "PTO EL TRIUNFO" },
    { departamento: "11", municipio: "26", codigo: "15", nombre: "SAN AGUSTÍN" },
    { departamento: "11", municipio: "24", codigo: "16", nombre: "SN BUENAVENTURA" },
    { departamento: "11", municipio: "25", codigo: "17", nombre: "SAN DIONISIO" },
    { departamento: "11", municipio: "25", codigo: "18", nombre: "SANTA ELENA" },
    { departamento: "11", municipio: "26", codigo: "19", nombre: "SAN FCO JAVIER" },
    { departamento: "11", municipio: "25", codigo: "20", nombre: "SANTA MARÍA" },
    { departamento: "11", municipio: "24", codigo: "21", nombre: "STGO DE MARÍA" },
    { departamento: "11", municipio: "25", codigo: "22", nombre: "TECAPÁN" },
    { departamento: "11", municipio: "25", codigo: "23", nombre: "USULUTÁN" },
    { departamento: "12", municipio: "21", codigo: "01", nombre: "CAROLINA" },
    { departamento: "12", municipio: "21", codigo: "02", nombre: "CIUDAD BARRIOS" },
    { departamento: "12", municipio: "22", codigo: "03", nombre: "COMACARÁN" },
    { departamento: "12", municipio: "21", codigo: "04", nombre: "CHAPELTIQUE" },
    { departamento: "12", municipio: "23", codigo: "05", nombre: "CHINAMECA" },
    { departamento: "12", municipio: "22", codigo: "06", nombre: "CHIRILAGUA" },
    { departamento: "12", municipio: "23", codigo: "07", nombre: "EL TRANSITO" },
    { departamento: "12", municipio: "23", codigo: "08", nombre: "LOLOTIQUE" },
    { departamento: "12", municipio: "22", codigo: "09", nombre: "MONCAGUA" },
    { departamento: "12", municipio: "23", codigo: "10", nombre: "NUEVA GUADALUPE" },
    { departamento: "12", municipio: "21", codigo: "11", nombre: "NVO EDÉN S JUAN" },
    { departamento: "12", municipio: "22", codigo: "12", nombre: "QUELEPA" },
    { departamento: "12", municipio: "21", codigo: "13", nombre: "SAN ANT D MOSCO" },
    { departamento: "12", municipio: "21", codigo: "14", nombre: "SAN GERARDO" },
    { departamento: "12", municipio: "23", codigo: "15", nombre: "SAN JORGE" },
    { departamento: "12", municipio: "21", codigo: "16", nombre: "SAN LUIS REINA" },
    { departamento: "12", municipio: "22", codigo: "17", nombre: "SAN MIGUEL" },
    { departamento: "12", municipio: "23", codigo: "18", nombre: "SAN RAF ORIENTE" },
    { departamento: "12", municipio: "21", codigo: "19", nombre: "SESORI" },
    { departamento: "12", municipio: "22", codigo: "20", nombre: "ULUAZAPA" },
    { departamento: "13", municipio: "27", codigo: "01", nombre: "ARAMBALA" },
    { departamento: "13", municipio: "27", codigo: "02", nombre: "CACAOPERA" },
    { departamento: "13", municipio: "27", codigo: "03", nombre: "CORINTO" },
    { departamento: "13", municipio: "28", codigo: "04", nombre: "CHILANGA" },
    { departamento: "13", municipio: "28", codigo: "05", nombre: "DELIC DE CONCEP" },
    { departamento: "13", municipio: "28", codigo: "06", nombre: "EL DIVISADERO" },
    { departamento: "13", municipio: "27", codigo: "07", nombre: "EL ROSARIO" },
    { departamento: "13", municipio: "28", codigo: "08", nombre: "GUALOCOCTI" },
    { departamento: "13", municipio: "28", codigo: "09", nombre: "GUATAJIAGUA" },
    { departamento: "13", municipio: "27", codigo: "10", nombre: "JOATECA" },
    { departamento: "13", municipio: "27", codigo: "11", nombre: "JOCOAITIQUE" },
    { departamento: "13", municipio: "28", codigo: "12", nombre: "JOCORO" },
    { departamento: "13", municipio: "28", codigo: "13", nombre: "LOLOTIQUILLO" },
    { departamento: "13", municipio: "27", codigo: "14", nombre: "MEANGUERA" },
    { departamento: "13", municipio: "28", codigo: "15", nombre: "OSICALA" },
    { departamento: "13", municipio: "27", codigo: "16", nombre: "PERQUÍN" },
    { departamento: "13", municipio: "28", codigo: "17", nombre: "SAN CARLOS" },
    { departamento: "13", municipio: "27", codigo: "18", nombre: "SAN FERNANDO" },
    { departamento: "13", municipio: "28", codigo: "19", nombre: "SAN FCO GOTERA" },
    { departamento: "13", municipio: "27", codigo: "20", nombre: "SAN ISIDRO" },
    { departamento: "13", municipio: "28", codigo: "21", nombre: "SAN SIMÓN" },
    { departamento: "13", municipio: "28", codigo: "22", nombre: "SENSEMBRA" },
    { departamento: "13", municipio: "28", codigo: "23", nombre: "SOCIEDAD" },
    { departamento: "13", municipio: "27", codigo: "24", nombre: "TOROLA" },
    { departamento: "13", municipio: "28", codigo: "25", nombre: "YAMABAL" },
    { departamento: "13", municipio: "28", codigo: "26", nombre: "YOLOAIQUÍN" },
    { departamento: "14", municipio: "19", codigo: "01", nombre: "ANAMOROS" },
    { departamento: "14", municipio: "19", codigo: "02", nombre: "BOLÍVAR" },
    { departamento: "14", municipio: "19", codigo: "03", nombre: "CONCEP DE OTE" },
    { departamento: "14", municipio: "20", codigo: "04", nombre: "CONCHAGUA" },
    { departamento: "14", municipio: "20", codigo: "05", nombre: "EL CARMEN" },
    { departamento: "14", municipio: "19", codigo: "06", nombre: "EL SAUCE" },
    { departamento: "14", municipio: "20", codigo: "07", nombre: "INTIPUCÁ" },
    { departamento: "14", municipio: "20", codigo: "08", nombre: "LA UNIÓN" },
    { departamento: "14", municipio: "19", codigo: "09", nombre: "LISLIQUE" },
    { departamento: "14", municipio: "20", codigo: "10", nombre: "MEANG DEL GOLFO" },
    { departamento: "14", municipio: "19", codigo: "11", nombre: "NUEVA ESPARTA" },
    { departamento: "14", municipio: "19", codigo: "12", nombre: "PASAQUINA" },
    { departamento: "14", municipio: "19", codigo: "13", nombre: "POLORÓS" },
    { departamento: "14", municipio: "20", codigo: "14", nombre: "SAN ALEJO" },
    { departamento: "14", municipio: "19", codigo: "15", nombre: "SAN JOSE" },
    { departamento: "14", municipio: "19", codigo: "16", nombre: "SANTA ROSA LIMA" },
    { departamento: "14", municipio: "20", codigo: "17", nombre: "YAYANTIQUE" },
    { departamento: "14", municipio: "20", codigo: "18", nombre: "YUCUAIQUÍN" },
  ];

  const municipiosFiltrados = useMemo(() => {
    if (!departamento) return [];
    return todosMunicipios.filter(m => m.departamento === departamento);
  }, [departamento]);

  // ✅ FILTRAR DISTRITOS: dependen de Departamento Y Municipio (cascada real)
  const distritosFiltrados = useMemo(() => {
    if (!departamento || !municipio) return [];
    return todosDistritos.filter(
      d => d.departamento === departamento && d.municipio === municipio
    );
  }, [departamento, municipio]);

  useEffect(() => {
    const fetchActividades = async () => {
      try {
        setLoadingActividades(true);
        const res = await api.get("/actividades-economicas");
        const actividadesData = Array.isArray(res.data) ? res.data : [];
        setActividades(actividadesData);
        setFilteredActividades(actividadesData);
      } catch (error) {
        console.error("Error cargando actividades:", error);
        Swal.fire({
          icon: "warning",
          title: "Advertencia",
          text: "No se pudieron cargar las actividades económicas",
          confirmButtonText: "Continuar",
        });
      } finally {
        setLoadingActividades(false);
      }
    };

    fetchActividades();
  }, []);

  useEffect(() => {
    if (actividadSearch.trim() === '') {
      setFilteredActividades(actividades);
    } else {
      const searchLower = actividadSearch.toLowerCase();
      const filtered = actividades.filter(act => 
        act.descripcion.toLowerCase().includes(searchLower) ||
        act.codigo.toLowerCase().includes(searchLower)
      );
      setFilteredActividades(filtered);
    }
  }, [actividadSearch, actividades]);

  useEffect(() => {
    if (tipoCliente === "Contribuyente") {
      setTipoDocumento("36");
    } else {
      setTipoDocumento("");
    }
  }, [tipoCliente]);

  useEffect(() => {
    if (!isLoadingClient.current) {
      setMunicipio("");
    }
  }, [departamento]);

  // ✅ Al cambiar el municipio, el distrito elegido ya no es válido (igual que en SupplierForm)
  useEffect(() => {
    if (!isLoadingClient.current) {
      setDistrito("");
    }
  }, [municipio]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActividadDropdown && !event.target.closest('.actividad-search-container')) {
        setShowActividadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActividadDropdown]);

  useEffect(() => {
    if (editingClient) {
      isLoadingClient.current = true;

      setNombre(editingClient.nombre || "");
      setNrc(editingClient.nrc || "");
      setDireccion(editingClient.direccion || "");
      setTelefono(editingClient.telefono || "");
      setEmail(editingClient.email || "");
      setTipoCliente(editingClient.tipo_cliente || "No Contribuyente");
      setDepartamento(editingClient.departamento || "");
      setMunicipio(editingClient.municipio || "");
      setDistrito(editingClient.distrito || "");
      setNombreComercial(editingClient.nombre_comercial || "");
      setTipoDocumento(editingClient.tipo_documento || "");
      setNumeroDocumento(editingClient.numero_documento || "");
      setCodActividad(editingClient.cod_actividad || "");
      setDescActividad(editingClient.desc_actividad || "");
      setActividadSearch(editingClient.desc_actividad || "");

      setTimeout(() => {
        isLoadingClient.current = false;
      }, 100);
    } else {
      resetForm();
    }

    setTimeout(() => nombreRef.current?.focus(), 100);
  }, [editingClient]);

  const resetForm = () => {
    setNombre("");
    setNrc("");
    setDireccion("");
    setTelefono("");
    setEmail("");
    setTipoCliente("No Contribuyente");
    setDepartamento("");
    setMunicipio("");
    setDistrito("");
    setNombreComercial("");
    setTipoDocumento("");
    setNumeroDocumento("");
    setCodActividad("");
    setDescActividad("");
    setActividadSearch("");
    
    setTimeout(() => nombreRef.current?.focus(), 50);
  };

  const handleActividadSelect = (actividad) => {
    setCodActividad(actividad.codigo);
    setDescActividad(actividad.descripcion);
    setActividadSearch(actividad.descripcion);
    setShowActividadDropdown(false);
  };

  const handleActividadInputChange = (e) => {
    const value = e.target.value;
    setActividadSearch(value);
    setShowActividadDropdown(true);
    
    if (value.trim() === '') {
      setCodActividad('');
      setDescActividad('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (tipoCliente === "Contribuyente") {
      if (!tipoDocumento || !numeroDocumento) {
        Swal.fire("Error", "El tipo y número de documento son obligatorios para contribuyentes", "error");
        return;
      }
      
      if (tipoDocumento === "36") {
        if (!validarNIT(numeroDocumento)) {
          Swal.fire({
            icon: "error",
            title: "NIT Inválido",
            html: `
              <p>El NIT debe tener uno de estos formatos:</p>
              <ul style="text-align: left; margin: 0 auto; display: inline-block;">
                <li><strong>Formato largo:</strong> 0000-000000-000-0 (14 dígitos)</li>
                <li><strong>Formato corto:</strong> 00000000-0 (9 dígitos)</li>
              </ul>
            `,
          });
          return;
        }
      }
      
      if (!codActividad) {
        Swal.fire("Error", "Debe seleccionar una actividad económica", "error");
        return;
      }
    }

    const payload = { 
      nombre, 
      tipo_cliente: tipoCliente,
    };

    if (tipoDocumento && tipoDocumento.trim() !== '') {
      payload.tipo_documento = tipoDocumento;
    }
    
    if (numeroDocumento && numeroDocumento.trim() !== '') {
      payload.numero_documento = numeroDocumento.replace(/-/g, '');
    } else {
      delete payload.tipo_documento;
    }

    if (telefono && telefono.trim() !== '') payload.telefono = telefono;
    if (email && email.trim() !== '') payload.email = email;
    if (direccion && direccion.trim() !== '') payload.direccion = direccion;
    if (departamento && departamento.trim() !== '') payload.departamento = departamento;
    if (municipio && municipio.trim() !== '') payload.municipio = municipio;
    if (distrito && distrito.trim() !== '') payload.distrito = distrito;

    if (tipoCliente === "Contribuyente") {
      payload.nrc = nrc.replace(/-/g, '');
      payload.nombre_comercial = nombreComercial || null;
      payload.cod_actividad = codActividad;
      payload.desc_actividad = descActividad;
    }

    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, payload);
        Swal.fire("Actualizado", "Cliente actualizado correctamente", "success");
      } else {
        await api.post("/clients", payload);
        Swal.fire("Guardado", "Cliente registrado correctamente", "success");
      }

      resetForm();
      setEditingClient(null);
      if (typeof fetchClients === "function") fetchClients();
      if (typeof onCancel === "function") onCancel();
    } catch (error) {
      if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0][0];
        Swal.fire("Error", firstError, "error");
      } else {
        Swal.fire("Error", "Ocurrió un error al guardar el cliente", "error");
        console.error(error);
      }
    }
  };

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <UserPlus size={24} color="#667eea" />
          <h2 className="card-title" style={{ fontSize: "20px" }}>
            {editingClient ? "Editar Cliente" : "Nuevo Cliente"}
          </h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Tipo de Cliente *</label>
              <select className="form-control" value={tipoCliente} onChange={(e) => setTipoCliente(e.target.value)}>
                <option value="No Contribuyente">No Contribuyente</option>
                <option value="Contribuyente">Contribuyente</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Nombre Completo *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ingrese el nombre del cliente"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                ref={nombreRef}
                required
              />
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
            Documento de Identificación {tipoCliente === "Contribuyente" && <span style={{ color: "#e53e3e" }}>*</span>}
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">
                Tipo de Documento {tipoCliente === "Contribuyente" && <span style={{ color: "#e53e3e" }}>*</span>}
              </label>
              <select
                className="form-control"
                value={tipoDocumento}
                onChange={(e) => setTipoDocumento(e.target.value)}
                required={tipoCliente === "Contribuyente"}
              >
                <option value="">Seleccione tipo de documento...</option>
                {tiposDocumento.map((tipo) => (
                  <option 
                    key={tipo.codigo} 
                    value={tipo.codigo}
                    disabled={tipoCliente === "Contribuyente" && tipo.codigo !== "36"}
                  >
                    {tipo.nombre}
                    {tipoCliente === "Contribuyente" && tipo.codigo !== "36" && " (No permitido para contribuyentes)"}
                  </option>
                ))}
              </select>
              {tipoCliente === "Contribuyente" && (
                <small style={{ color: "#ed8936", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ Los contribuyentes solo pueden usar NIT
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Número de Documento {tipoCliente === "Contribuyente" && <span style={{ color: "#e53e3e" }}>*</span>}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={
                  tipoDocumento === "13" ? "00000000-0" :
                  tipoDocumento === "36" ? "0000-000000-000-0 o 00000000-0" :
                  "Ingrese el número del documento"
                }
                value={numeroDocumento}
                onChange={(e) => {
                  let valor = e.target.value;
                  
                  if (tipoDocumento === "36") {
                    valor = formatearNIT(valor);
                  } else if (tipoDocumento === "13") {
                    valor = formatearDUI(valor);
                  }
                  
                  setNumeroDocumento(valor);
                  
                  if (!valor && tipoCliente === "No Contribuyente") {
                    setTipoDocumento("");
                  }
                }}
                maxLength={tipoDocumento === "36" ? 17 : tipoDocumento === "13" ? 10 : undefined}
                required={tipoCliente === "Contribuyente"}
              />
              <small style={{ color: "#718096", fontSize: "12px" }}>
                {tipoDocumento === "13" && "Formato DUI: 00000000-0"}
                {tipoDocumento === "36" && "Formato NIT: 0000-000000-000-0 (14 dígitos) o 00000000-0 (9 dígitos)"}
                {tipoDocumento === "03" && "Número de pasaporte"}
                {tipoDocumento === "02" && "Número de carnet de residente"}
                {tipoDocumento === "37" && "Otro tipo de documento"}
              </small>
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {tipoCliente === "Contribuyente" && (
            <>
              <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
                Información Fiscal
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                <div className="form-group">
                  <label className="form-label">NRC *</label>
                  <input
                    className="form-control"
                    placeholder="000000-0"
                    value={nrc}
                    onChange={(e) => setNrc(e.target.value)}
                    required
                  />
                  <small style={{ color: "#718096", fontSize: "12px" }}>
                    Puede incluir guión (se guardará sin guión)
                  </small>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Nombre Comercial</label>
                  <input
                    className="form-control"
                    placeholder="Nombre del negocio (opcional)"
                    value={nombreComercial}
                    onChange={(e) => setNombreComercial(e.target.value)}
                  />
                </div>

                <div className="form-group actividad-search-container" style={{ gridColumn: "1 / -1", position: "relative" }}>
                  <label className="form-label">Actividad Económica (CIIU) *</label>
                  {loadingActividades ? (
                    <div style={{ padding: "12px", textAlign: "center" }}>
                      <small style={{ color: "#718096" }}>Cargando actividades...</small>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar actividad económica por código o descripción..."
                        value={actividadSearch}
                        onChange={handleActividadInputChange}
                        onFocus={() => setShowActividadDropdown(true)}
                        required
                      />
                      
                      {showActividadDropdown && (
                        <div style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          maxHeight: "300px",
                          overflowY: "auto",
                          background: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                          zIndex: 1000,
                          marginTop: "4px"
                        }}>
                          {filteredActividades.length === 0 ? (
                            <div style={{ 
                              padding: "16px", 
                              textAlign: "center", 
                              color: "#718096" 
                            }}>
                              No se encontraron actividades
                            </div>
                          ) : (
                            filteredActividades.map((actividad) => (
                              <div
                                key={actividad.codigo}
                                onClick={() => handleActividadSelect(actividad)}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #f1f5f9",
                                  transition: "background 0.2s",
                                }}
                                onMouseEnter={(e) => e.target.style.background = "#f8fafc"}
                                onMouseLeave={(e) => e.target.style.background = "white"}
                              >
                                <div style={{ fontWeight: 500, color: "#1e293b", marginBottom: "4px" }}>
                                  {actividad.codigo}
                                </div>
                                <div style={{ fontSize: "13px", color: "#64748b" }}>
                                  {actividad.descripcion}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {descActividad && (
                  <div style={{ 
                    gridColumn: "1 / -1", 
                    padding: "12px", 
                    background: "#f0f9ff", 
                    borderRadius: "8px", 
                    border: "1px solid #bae6fd",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <small style={{ color: "#0369a1", fontSize: "13px", fontWeight: 500 }}>
                      📋 Seleccionada: {codActividad} - {descActividad}
                    </small>
                    <button
                      type="button"
                      onClick={() => {
                        setCodActividad('');
                        setDescActividad('');
                        setActividadSearch('');
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#0369a1",
                        cursor: "pointer",
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                    >
                      ✕ Limpiar
                    </button>
                  </div>
                )}
              </div>

              <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>
            </>
          )}

          <h4 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "#2d3748" }}>
            Información de Contacto
          </h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-control" placeholder="0000-0000" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Correo Electrónico</label>
              <input type="email" className="form-control" placeholder="correo@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <div className="form-group">
              <label className="form-label">Dirección</label>
              <input className="form-control" placeholder="Dirección completa" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Departamento</label>
              <select 
                className="form-control" 
                value={departamento} 
                onChange={(e) => setDepartamento(e.target.value)}
              >
                <option value="">Seleccione...</option>
                {departamentos.map((depto) => (
                  <option key={depto.codigo} value={depto.codigo}>{depto.nombre}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Municipio</label>
              <select 
                className="form-control" 
                value={municipio} 
                onChange={(e) => setMunicipio(e.target.value)}
                disabled={!departamento}
              >
                <option value="">
                  {departamento ? "Seleccione..." : "Primero seleccione departamento"}
                </option>
                {municipiosFiltrados.map((muni) => (
                  <option key={`${muni.departamento}-${muni.codigo}`} value={muni.codigo}>
                    {muni.nombre}
                  </option>
                ))}
              </select>
              {departamento && municipiosFiltrados.length === 0 && (
                <small style={{ color: "#e53e3e", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ No hay municipios disponibles para este departamento
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Distrito</label>
              <select
                className="form-control"
                value={distrito}
                onChange={(e) => setDistrito(e.target.value)}
                disabled={!municipio}
              >
                <option value="">
                  {municipio ? "Seleccione..." : "Primero seleccione municipio"}
                </option>
                {distritosFiltrados.map((dist) => (
                  <option key={`${dist.departamento}-${dist.municipio}-${dist.codigo}`} value={dist.codigo}>
                    {dist.nombre}
                  </option>
                ))}
              </select>
              {municipio && distritosFiltrados.length === 0 && (
                <small style={{ color: "#e53e3e", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ No hay distritos disponibles para este municipio
                </small>
              )}
            </div>
          </div>

          <div className="d-flex gap-2" style={{ marginTop: "32px" }}>
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              {editingClient ? "Actualizar Cliente" : "Guardar Cliente"}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                resetForm();
                setEditingClient(null);
                if (typeof onCancel === "function") onCancel();
              }}
            >
              <X size={18} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}